// CircularTimer/index.tsx
import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { TimerContainer, SVG, CircleBackground, CircleProgress, TimeText } from './styles';

interface CircularTimerProps {
  totalSeconds: number;
  onTimerEnd?: () => void;
  size?: number;
  strokeWidth?: number;
}

export const CircularTimer: React.FC<CircularTimerProps> = ({
  totalSeconds,
  onTimerEnd,
  size = 80,
  strokeWidth = 8,
}) => {
  const [remainingSeconds, setRemainingSeconds] = useState(totalSeconds);

  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;

  useEffect(() => {
    setRemainingSeconds(totalSeconds);

    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onTimerEnd?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [totalSeconds, onTimerEnd]);

  const progress = remainingSeconds / totalSeconds;
  const strokeDashoffset = circumference * (progress - 1);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <TimerContainer size={size}>
      <SVG width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <CircleBackground
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <CircleProgress
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
        />
      </SVG>
      <TimeText>
        {formatTime(remainingSeconds)}
      </TimeText>
    </TimerContainer>
  );
};